import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Landmark, ShieldCheck, ShieldAlert, ShieldQuestion, Wallet } from "lucide-react";

const STATUS_META = {
  pending: { label: "Under review", icon: ShieldQuestion, cls: "bg-amber-100 text-amber-800" },
  verified: { label: "Verified", icon: ShieldCheck, cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected — please resubmit", icon: ShieldAlert, cls: "bg-rose-100 text-rose-700" },
};

export default function OwnerBank() {
  const [bank, setBank] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ account_holder: "", account_number: "", ifsc: "", bank_name: "", upi_id: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/owner/bank").then((r) => {
    setBank(r.data);
    if (!r.data.has_bank) setEditing(true);
  });
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/owner/bank", form);
      toast.success("Bank details submitted — verification is now under review");
      setEditing(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save bank details");
    } finally {
      setSaving(false);
    }
  };

  if (!bank) return null;
  const status = STATUS_META[bank.verified_status] || STATUS_META.pending;
  const StatusIcon = status.icon;

  return (
    <section className="mt-10">
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Payouts</div>
          <h2 className="font-display font-black text-3xl tracking-tighter mt-1 flex items-center gap-2">
            <Landmark size={26} className="text-orange-800" /> Bank account for payouts
          </h2>
        </div>
        {bank.has_bank && (
          <Badge className={`gap-1 ${status.cls}`}><StatusIcon size={14} /> {status.label}</Badge>
        )}
      </div>

      {!editing && bank.has_bank ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Account holder</div>
              <div className="mt-1 font-semibold">{bank.account_holder}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Bank</div>
              <div className="mt-1 font-semibold">{bank.bank_name || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Account number</div>
              <div className="mt-1 font-mono">{bank.account_number_masked}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">IFSC</div>
              <div className="mt-1 font-mono">{bank.ifsc}</div>
            </div>
            {bank.upi_id && (
              <div className="sm:col-span-2">
                <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">UPI ID</div>
                <div className="mt-1 font-semibold">{bank.upi_id}</div>
              </div>
            )}
          </div>
          <div className="mt-6 pt-4 border-t border-dashed border-slate-200 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-500 flex items-center gap-1"><Wallet size={12} /> Payouts to this account are settled every Monday for the previous week's bookings.</p>
            <Button data-testid="edit-bank-btn" variant="outline" size="sm" onClick={() => { setForm({ ...form, account_holder: bank.account_holder, ifsc: bank.ifsc, bank_name: bank.bank_name, upi_id: bank.upi_id }); setEditing(true); }} className="rounded-full">
              Update bank details
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="text-xs tracking-widest uppercase font-bold">Account holder name (must match PAN)</Label>
            <Input data-testid="bank-holder" required value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} placeholder="Ramesh Sharma" className="mt-1 h-11" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase font-bold">Bank name</Label>
            <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="HDFC Bank" className="mt-1 h-11" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase font-bold">IFSC code</Label>
            <Input data-testid="bank-ifsc" required maxLength={11} value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} placeholder="HDFC0001234" className="mt-1 h-11 font-mono" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase font-bold">Account number</Label>
            <Input data-testid="bank-account" required inputMode="numeric" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/\D/g, "") })} placeholder="9-18 digits" className="mt-1 h-11 font-mono" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase font-bold">UPI ID (optional, for instant transfers)</Label>
            <Input value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} placeholder="ramesh@hdfcbank" className="mt-1 h-11" />
          </div>
          <div className="sm:col-span-2 flex items-center justify-between pt-2">
            <p className="text-xs text-slate-500 max-w-md">
              We do a ₹1 penny-drop check to verify your account name matches the bank record. Only verified accounts receive payouts.
            </p>
            <div className="flex gap-2">
              {bank.has_bank && (
                <Button type="button" variant="outline" onClick={() => setEditing(false)} className="rounded-full">Cancel</Button>
              )}
              <Button data-testid="bank-submit" type="submit" disabled={saving} className="rounded-full bg-orange-800 hover:bg-orange-900 text-white active:scale-95 transition-transform">
                {saving ? "Saving…" : bank.has_bank ? "Update & re-verify" : "Save & verify"}
              </Button>
            </div>
          </div>
        </form>
      )}
    </section>
  );
}
